"use client";

import { useState } from "react";
import { api } from "@/lib/api";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { PageHeader, LoadingState, ErrorState, EmptyState } from "@/components/features/states";
import { RevenueMixCard } from "@/components/features/revenue-mix-card";
import { formatPrice } from "@/lib/format";
import {
  BarChart3,
  TrendingUp,
  Users as UsersIcon,
  Mail,
  Megaphone,
  Flag,
  Percent,
  Banknote,
  Package,
} from "lucide-react";
import {
  AreaChart,
  Area,
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { useTranslation } from "react-i18next";

const RANGE_KEYS = ["7d", "30d", "90d"] as const;
const RANGE_LABEL_KEYS: Record<string, string> = {
  "7d": "admin.analytics.range7d",
  "30d": "admin.analytics.range30d",
  "90d": "admin.analytics.range90d",
};

// Emerald/amber palette aligned with the design system.
const CHART_COLORS = {
  emerald: "oklch(0.62 0.14 162)",
  amber: "oklch(0.72 0.15 70)",
  sky: "oklch(0.6 0.12 200)",
  violet: "oklch(0.55 0.2 320)",
  orange: "oklch(0.7 0.18 40)",
};

function ChartCard({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  // title/subtitle are pre-translated strings passed in by the parent.
  return (
    <Card>
      <CardContent className="p-4">
        <div className="mb-3">
          <h3 className="text-sm font-semibold">{title}</h3>
          {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
        </div>
        {children}
      </CardContent>
    </Card>
  );
}

function KpiCard({
  icon: Icon,
  label,
  value,
  color,
  hint,
}: {
  icon: React.ElementType;
  label: string;
  value: React.ReactNode;
  color: string;
  hint?: string;
}) {
  return (
    <Card>
      <CardContent className="flex flex-col gap-1 p-4">
        <div className="flex items-center gap-2">
          <Icon className={`h-4 w-4 ${color}`} />
          <span className="text-xs text-muted-foreground">{label}</span>
        </div>
        <p className="text-xl font-bold">{value}</p>
        {hint && <p className="text-[10px] text-muted-foreground">{hint}</p>}
      </CardContent>
    </Card>
  );
}

function EmptyChart({ label }: { label: string }) {
  return (
    <div className="flex h-40 items-center justify-center text-xs text-muted-foreground">
      {label}
    </div>
  );
}

export function AdminAnalytics() {
  const { t } = useTranslation();
  const [range, setRange] = useState("30d");

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["admin", "analytics", range],
    queryFn: () => api.admin.analytics({ range }),
    refetchInterval: 30000,
  });
  const a = data?.data?.analytics;

  if (isLoading) return <LoadingState label={t("admin.analytics.loadingAnalytics")} />;
  if (error || !data?.ok) return <ErrorState message={data?.error || t("admin.analytics.loadFailed")} onRetry={refetch} />;
  if (!a) return <EmptyState icon={<BarChart3 className="h-8 w-8" />} title={t("admin.analytics.noAnalytics")} />;

  const jobsSeries = (a.jobsTimeSeries ?? []) as { date: string; count: number }[];
  const revenueSeries = (a.revenueTimeSeries ?? []) as { date: string; amount: number }[];
  const usersSeries = (a.usersTimeSeries ?? []) as { date: string; count: number }[];
  const verticals = (a.verticalBreakdown ?? []) as {
    vertical: string;
    count: number;
    revenue?: number;
  }[];
  const topProviders = (a.topProviders ?? []) as {
    name: string;
    jobsCompleted?: number;
    revenue?: number;
    payout?: number;
  }[];
  const emailsByTemplate = (a.emailsByTemplate ?? []) as { template: string; count: number }[];
  const adPerf = (a.adPerformance ?? []) as {
    title: string;
    impressions: number;
    clicks: number;
  }[];
  const fraudByRule = (a.fraudByRule ?? []) as { rule: string; count: number }[];
  const commissionByVertical = (a.commissionByVertical ?? []) as {
    vertical: string;
    commission: number;
  }[];

  const totalEmails = (a.emailSentTotal ?? 0) as number;
  const openFraud = (a.fraudOpenCount ?? a.openFraudFlags ?? 0) as number;
  const resolvedFraud = (a.fraudResolvedCount ?? 0) as number;
  const totalCommission = (a.totalCommission ?? 0) as number;

  const totalJobsCreated = jobsSeries.reduce((s, x) => s + (x.count || 0), 0);
  const totalRevenue = revenueSeries.reduce((s, x) => s + (x.amount || 0), 0);
  const totalNewUsers = usersSeries.reduce((s, x) => s + (x.count || 0), 0);
  const totalAdImpressions = adPerf.reduce((s, x) => s + (x.impressions || 0), 0);
  const totalAdClicks = adPerf.reduce((s, x) => s + (x.clicks || 0), 0);

  const fmtShortDate = (d: string) => {
    try {
      const dt = new Date(d);
      return `${dt.getMonth() + 1}/${dt.getDate()}`;
    } catch {
      return d;
    }
  };

  const fmtPct = (n: number) =>
    `${n.toLocaleString(undefined, { maximumFractionDigits: 1 })}%`;

  return (
    <div className="space-y-4">
      <PageHeader
        title={t("admin.analytics.title")}
        subtitle={t("admin.analytics.subtitle")}
        action={
          <Tabs value={range} onValueChange={setRange}>
            <TabsList>
              {RANGE_KEYS.map((r) => (
                <TabsTrigger key={r} value={r}>
                  {t(RANGE_LABEL_KEYS[r])}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        }
      />

      {/* Top KPIs */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        <KpiCard
          icon={Package}
          label={t("admin.analytics.kpiJobsCreated")}
          value={totalJobsCreated}
          color="text-primary"
        />
        <KpiCard
          icon={Banknote}
          label={t("admin.analytics.kpiRevenue")}
          value={formatPrice(totalRevenue)}
          color="text-emerald-600"
        />
        <KpiCard
          icon={UsersIcon}
          label={t("admin.analytics.kpiNewUsers")}
          value={totalNewUsers}
          color="text-sky-600"
        />
        <KpiCard
          icon={Percent}
          label={t("admin.analytics.kpiCommission")}
          value={formatPrice(totalCommission)}
          color="text-amber-600"
        />
        <KpiCard
          icon={Mail}
          label={t("admin.analytics.kpiEmailsSent")}
          value={totalEmails}
          color="text-violet-600"
        />
        <KpiCard
          icon={Flag}
          label={t("admin.analytics.kpiFraudOpen")}
          value={openFraud}
          color="text-red-600"
          hint={t("admin.analytics.kpiFraudResolvedHint", { count: resolvedFraud })}
        />
        <KpiCard
          icon={Megaphone}
          label={t("admin.analytics.kpiAdImpressions")}
          value={totalAdImpressions}
          color="text-amber-600"
        />
        <KpiCard
          icon={TrendingUp}
          label={t("admin.analytics.kpiAdClicks")}
          value={totalAdClicks}
          color="text-emerald-600"
        />
      </div>

      {/* V1.0 Monetization — Module 3: Revenue Mix + Launch Partner alerts */}
      <RevenueMixCard range={range} />

      {/* Time series */}
      <div className="grid gap-3 lg:grid-cols-2">
        <ChartCard title={t("admin.analytics.chartJobsCreatedTitle")} subtitle={t("admin.analytics.chartJobsCreatedSubtitle")}>
          {jobsSeries.length === 0 ? (
            <EmptyChart label={t("admin.analytics.emptyNoDataForRange")} />
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={jobsSeries.map((x) => ({ date: fmtShortDate(x.date), count: x.count || 0 }))}>
                <defs>
                  <linearGradient id="jobsGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={CHART_COLORS.emerald} stopOpacity={0.4} />
                    <stop offset="95%" stopColor={CHART_COLORS.emerald} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis dataKey="date" stroke="var(--muted-foreground)" fontSize={11} tickLine={false} />
                <YAxis stroke="var(--muted-foreground)" fontSize={11} tickLine={false} allowDecimals={false} />
                <Tooltip
                  contentStyle={{
                    background: "var(--popover)",
                    border: "1px solid var(--border)",
                    borderRadius: 8,
                    fontSize: 12,
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="count"
                  stroke={CHART_COLORS.emerald}
                  strokeWidth={2}
                  fill="url(#jobsGrad)"
                  name={t("admin.analytics.legendJobs")}
                />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        <ChartCard title={t("admin.analytics.chartRevenueTitle")} subtitle={t("admin.analytics.chartRevenueSubtitle")}>
          {revenueSeries.length === 0 ? (
            <EmptyChart label={t("admin.analytics.emptyNoDataForRange")} />
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={revenueSeries.map((x) => ({ date: fmtShortDate(x.date), amount: x.amount || 0 }))}>
                <defs>
                  <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={CHART_COLORS.amber} stopOpacity={0.4} />
                    <stop offset="95%" stopColor={CHART_COLORS.amber} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis dataKey="date" stroke="var(--muted-foreground)" fontSize={11} tickLine={false} />
                <YAxis stroke="var(--muted-foreground)" fontSize={11} tickLine={false} />
                <Tooltip
                  contentStyle={{
                    background: "var(--popover)",
                    border: "1px solid var(--border)",
                    borderRadius: 8,
                    fontSize: 12,
                  }}
                  formatter={(v: number) => formatPrice(v)}
                />
                <Area
                  type="monotone"
                  dataKey="amount"
                  stroke={CHART_COLORS.amber}
                  strokeWidth={2}
                  fill="url(#revGrad)"
                  name={t("admin.analytics.legendRevenue")}
                />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
      </div>

      <ChartCard title={t("admin.analytics.chartNewUsersTitle")} subtitle={t("admin.analytics.chartNewUsersSubtitle")}>
        {usersSeries.length === 0 ? (
          <EmptyChart label={t("admin.analytics.emptyNoDataForRange")} />
        ) : (
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={usersSeries.map((x) => ({ date: fmtShortDate(x.date), count: x.count || 0 }))}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis dataKey="date" stroke="var(--muted-foreground)" fontSize={11} tickLine={false} />
              <YAxis stroke="var(--muted-foreground)" fontSize={11} tickLine={false} allowDecimals={false} />
              <Tooltip
                contentStyle={{
                  background: "var(--popover)",
                  border: "1px solid var(--border)",
                  borderRadius: 8,
                  fontSize: 12,
                }}
              />
              <Line
                type="monotone"
                dataKey="count"
                stroke={CHART_COLORS.sky}
                strokeWidth={2}
                dot={false}
                name={t("admin.analytics.legendUsers")}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </ChartCard>

      {/* Vertical breakdown */}
      <ChartCard title={t("admin.analytics.chartVerticalBreakdownTitle")} subtitle={t("admin.analytics.chartVerticalBreakdownSubtitle")}>
        {verticals.length === 0 ? (
          <EmptyChart label={t("admin.analytics.emptyNoData")} />
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={verticals.map((v) => ({ vertical: v.vertical, jobs: v.count || 0, revenue: v.revenue || 0 }))}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis dataKey="vertical" stroke="var(--muted-foreground)" fontSize={11} tickLine={false} />
              <YAxis stroke="var(--muted-foreground)" fontSize={11} tickLine={false} />
              <Tooltip
                contentStyle={{
                  background: "var(--popover)",
                  border: "1px solid var(--border)",
                  borderRadius: 8,
                  fontSize: 12,
                }}
                formatter={(v: number, name: string) =>
                  name === "revenue" ? formatPrice(v) : v
                }
              />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="jobs" fill={CHART_COLORS.emerald} radius={[4, 4, 0, 0]} name={t("admin.analytics.legendJobs")} />
              <Bar dataKey="revenue" fill={CHART_COLORS.amber} radius={[4, 4, 0, 0]} name={t("admin.analytics.legendRevenue")} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </ChartCard>

      {/* Top providers */}
      <ChartCard title={t("admin.analytics.chartTopProvidersTitle")} subtitle={t("admin.analytics.chartTopProvidersSubtitle")}>
        {topProviders.length === 0 ? (
          <EmptyChart label={t("admin.analytics.emptyNoProviderData")} />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">{t("admin.analytics.colRank")}</TableHead>
                <TableHead>{t("admin.analytics.colProvider")}</TableHead>
                <TableHead className="text-right">{t("admin.analytics.colJobs")}</TableHead>
                <TableHead className="text-right">{t("admin.analytics.colRevenue")}</TableHead>
                <TableHead className="text-right">{t("admin.analytics.colPayout")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {topProviders.map((p, i) => (
                <TableRow key={i}>
                  <TableCell className="font-mono text-xs">{i + 1}</TableCell>
                  <TableCell className="font-medium">{p.name}</TableCell>
                  <TableCell className="text-right">{p.jobsCompleted ?? 0}</TableCell>
                  <TableCell className="text-right">{formatPrice(p.revenue ?? 0)}</TableCell>
                  <TableCell className="text-right">{formatPrice(p.payout ?? 0)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </ChartCard>

      {/* Emails + Ads */}
      <div className="grid gap-3 lg:grid-cols-2">
        <ChartCard title={t("admin.analytics.chartEmailsByTemplateTitle")} subtitle={t("admin.analytics.chartEmailsByTemplateSubtitle")}>
          {emailsByTemplate.length === 0 ? (
            <EmptyChart label={t("admin.analytics.emptyNoEmailsSent")} />
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={emailsByTemplate.map((e) => ({ template: e.template, count: e.count || 0 }))} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis type="number" stroke="var(--muted-foreground)" fontSize={11} tickLine={false} allowDecimals={false} />
                <YAxis
                  type="category"
                  dataKey="template"
                  stroke="var(--muted-foreground)"
                  fontSize={10}
                  tickLine={false}
                  width={110}
                />
                <Tooltip
                  contentStyle={{
                    background: "var(--popover)",
                    border: "1px solid var(--border)",
                    borderRadius: 8,
                    fontSize: 12,
                  }}
                />
                <Bar dataKey="count" fill={CHART_COLORS.violet} radius={[0, 4, 4, 0]} name={t("admin.analytics.legendEmails")} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        <ChartCard title={t("admin.analytics.chartAdPerformanceTitle")} subtitle={t("admin.analytics.chartAdPerformanceSubtitle")}>
          {adPerf.length === 0 ? (
            <EmptyChart label={t("admin.analytics.emptyNoAdActivity")} />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("admin.analytics.colAd")}</TableHead>
                  <TableHead className="text-right">{t("admin.analytics.colImpr")}</TableHead>
                  <TableHead className="text-right">{t("admin.analytics.colClicks")}</TableHead>
                  <TableHead className="text-right">{t("admin.analytics.colCtr")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {adPerf.map((ad, i) => {
                  const ctr = ad.impressions > 0 ? (ad.clicks / ad.impressions) * 100 : 0;
                  return (
                    <TableRow key={i}>
                      <TableCell className="max-w-[160px] truncate font-medium">{ad.title}</TableCell>
                      <TableCell className="text-right">{ad.impressions}</TableCell>
                      <TableCell className="text-right">{ad.clicks}</TableCell>
                      <TableCell className="text-right">
                        <Badge variant="secondary">{fmtPct(ctr)}</Badge>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </ChartCard>
      </div>

      {/* Fraud + Commission */}
      <div className="grid gap-3 lg:grid-cols-2">
        <ChartCard title={t("admin.analytics.chartFraudByRuleTitle")} subtitle={t("admin.analytics.chartFraudByRuleSubtitle")}>
          {fraudByRule.length === 0 ? (
            <EmptyChart label={t("admin.analytics.emptyNoFraudTriggered")} />
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={fraudByRule.map((f) => ({ rule: f.rule, count: f.count || 0 }))}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis dataKey="rule" stroke="var(--muted-foreground)" fontSize={10} tickLine={false} interval={0} angle={-15} textAnchor="end" height={50} />
                <YAxis stroke="var(--muted-foreground)" fontSize={11} tickLine={false} allowDecimals={false} />
                <Tooltip
                  contentStyle={{
                    background: "var(--popover)",
                    border: "1px solid var(--border)",
                    borderRadius: 8,
                    fontSize: 12,
                  }}
                />
                <Bar dataKey="count" fill={CHART_COLORS.orange} radius={[4, 4, 0, 0]} name={t("admin.analytics.legendFlags")} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        <ChartCard title={t("admin.analytics.chartCommissionByVerticalTitle")} subtitle={t("admin.analytics.chartCommissionByVerticalSubtitle")}>
          {commissionByVertical.length === 0 ? (
            <EmptyChart label={t("admin.analytics.emptyNoCommissionEarned")} />
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={commissionByVertical.map((c) => ({ vertical: c.vertical, commission: c.commission || 0 }))}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis dataKey="vertical" stroke="var(--muted-foreground)" fontSize={11} tickLine={false} />
                <YAxis stroke="var(--muted-foreground)" fontSize={11} tickLine={false} />
                <Tooltip
                  contentStyle={{
                    background: "var(--popover)",
                    border: "1px solid var(--border)",
                    borderRadius: 8,
                    fontSize: 12,
                  }}
                  formatter={(v: number) => formatPrice(v)}
                />
                <Bar dataKey="commission" fill={CHART_COLORS.emerald} radius={[4, 4, 0, 0]} name={t("admin.analytics.legendCommission")} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
      </div>
    </div>
  );
}
